import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X, Plus, Trash2, Tag } from 'lucide-react';
import { useProfiles } from '@/hooks/useProfiles';
import { useWebsitePageTags } from '@/hooks/useWebsitePageTags';
import type { WebsiteTask, WebsiteName, TaskStatus, TaskPriority } from '@/types/website';
import { WEBSITE_LABELS, TASK_STATUS_LABELS, TASK_PRIORITY_LABELS } from '@/types/website';
import { cn } from '@/lib/utils';

interface TaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task?: WebsiteTask | null;
  onSave: (task: Partial<WebsiteTask>) => void;
  onDelete?: (id: string) => void;
  defaultWebsite?: WebsiteName;
}

export function TaskModal({ open, onOpenChange, task, onSave, onDelete, defaultWebsite }: TaskModalProps) {
  const { profiles } = useProfiles();
  const { data: availablePageTags = [] } = useWebsitePageTags();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState<WebsiteName>(defaultWebsite || 'greenvision');
  const [status, setStatus] = useState<TaskStatus>('idee');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [assignedTo, setAssignedTo] = useState<string>('none');
  const [dueDate, setDueDate] = useState('');
  const [labels, setLabels] = useState<string[]>([]);
  const [newLabel, setNewLabel] = useState('');
  const [pageTags, setPageTags] = useState<string[]>([]);
  const [newPageTag, setNewPageTag] = useState('');

  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description || '');
      setWebsite(task.website);
      setStatus(task.status);
      setPriority(task.priority);
      setAssignedTo(task.assigned_to || 'none');
      setDueDate(task.due_date || '');
      setLabels(task.labels || []);
      setPageTags(task.page_tags || []);
    } else {
      setTitle('');
      setDescription('');
      setWebsite(defaultWebsite || 'greenvision');
      setStatus('idee');
      setPriority('medium');
      setAssignedTo('none');
      setDueDate('');
      setLabels([]);
      setPageTags([]);
    }
  }, [task, defaultWebsite, open]);

  const handleSave = () => {
    if (!title.trim()) return;

    onSave({
      id: task?.id,
      title: title.trim(),
      description: description.trim() || null,
      website,
      status,
      priority,
      assigned_to: assignedTo === 'none' ? null : assignedTo,
      due_date: dueDate || null,
      labels,
      page_tags: pageTags,
      completed_at: status === 'voltooid' && task?.status !== 'voltooid' 
        ? new Date().toISOString() 
        : task?.completed_at,
    });
    onOpenChange(false);
  };

  const addLabel = () => {
    if (newLabel.trim() && !labels.includes(newLabel.trim())) {
      setLabels([...labels, newLabel.trim()]);
      setNewLabel('');
    }
  };

  const removeLabel = (label: string) => {
    setLabels(labels.filter((l) => l !== label));
  };

  const togglePageTag = (tag: string) => {
    if (pageTags.includes(tag)) {
      setPageTags(pageTags.filter(t => t !== tag));
    } else {
      setPageTags([...pageTags, tag]);
    }
  };

  const addNewPageTag = () => {
    const trimmed = newPageTag.trim();
    if (trimmed && !pageTags.includes(trimmed)) {
      setPageTags([...pageTags, trimmed]);
      setNewPageTag('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{task ? 'Taak bewerken' : 'Nieuwe taak'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titel *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Taak titel"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Omschrijving</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Beschrijf de taak..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Website</Label>
              <Select value={website} onValueChange={(v) => setWebsite(v as WebsiteName)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(WEBSITE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as TaskStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TASK_STATUS_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prioriteit</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TaskPriority)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TASK_PRIORITY_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Deadline</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Toegewezen aan</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer gebruiker" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Niemand</SelectItem>
                {profiles?.map((profile) => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {profile.display_name || profile.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Page Tags Section */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5" />
              Pagina's
            </Label>
            
            {/* Selected page tags */}
            {pageTags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {pageTags.map((tag) => (
                  <Badge key={tag} variant="default" className="gap-1">
                    {tag}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => togglePageTag(tag)}
                    />
                  </Badge>
                ))}
              </div>
            )}

            {/* Available page tags to select */}
            {availablePageTags.filter(t => !pageTags.includes(t)).length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {availablePageTags.filter(t => !pageTags.includes(t)).map((tag) => (
                  <Badge
                    key={tag}
                    variant="outline"
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => togglePageTag(tag)}
                  >
                    + {tag}
                  </Badge>
                ))}
              </div>
            )}

            {/* Add new page tag */}
            <div className="flex gap-2">
              <Input
                value={newPageTag}
                onChange={(e) => setNewPageTag(e.target.value)}
                placeholder="Nieuwe pagina..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addNewPageTag())}
                className="h-8"
              />
              <Button type="button" variant="outline" size="sm" className="h-8 px-2" onClick={addNewPageTag}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Labels</Label>
            <div className="flex gap-2">
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Nieuw label"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLabel())}
              />
              <Button type="button" variant="outline" size="icon" onClick={addLabel}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {labels.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {labels.map((label) => (
                  <Badge key={label} variant="secondary" className="gap-1">
                    {label}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => removeLabel(label)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          {task && onDelete && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                onDelete(task.id);
                onOpenChange(false);
              }}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Verwijderen
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSave} disabled={!title.trim()}>
              {task ? 'Opslaan' : 'Aanmaken'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
